import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { type DockStack, useDockStore, visibleStacks } from "../../stores/dock";
import { EmptyState } from "../common/EmptyState";
import { DockCard } from "./DockCard";
import { toCssPx } from "../../lib/uiScale";
import { DOCK_KINDS, type DockKindMap } from "./registry";
import styles from "./Dock.module.css";

/**
 * The dock (2026-07-15): a resizable right-hand GRID — columns side by side,
 * each a stack of cards.
 *
 * Three resize axes, two mechanisms, and the split is deliberate:
 *  - the DOCK resizes horizontally in pixels (the user is setting how much
 *    screen it gets, which is a pixel decision, and it persists);
 *  - COLUMNS and CARDS resize in flex WEIGHTS, so a split survives a window that
 *    changes size. Pixel sizes would need re-clamping on every resize and would
 *    drift out of sync with the real container.
 *
 * Renders whenever the dock is SHOWN — including with zero columns, which is the
 * summoned-from-cold path and draws the empty state. A dock that only appears
 * once something is already in it can never be found (lesson
 * `cold-path-unverified`).
 */

/** A card below this can't show its chrome plus any body worth seeing. It is the
 *  floor for the vertical drag, so a card can always be dragged back open. */
const MIN_CARD_PX = 96;
/** A column below this can't show a tab plus its chrome. Larger than
 *  MIN_CARD_PX because the chrome row is wide — tabs AND controls live on it. */
const MIN_STACK_PX = 220;

interface DockProps {
	/** Injectable for tests. Production always uses the real registry. */
	kinds?: DockKindMap;
}

export function Dock({ kinds = DOCK_KINDS }: DockProps) {
	const open = useDockStore((s) => s.open);
	const stacks = useDockStore((s) => s.stacks);
	const maximizedSlotId = useDockStore((s) => s.maximizedSlotId);
	const width = useDockStore((s) => s.width);
	const setWidth = useDockStore((s) => s.setWidth);
	const resizeSlotPair = useDockStore((s) => s.resizeSlotPair);
	const resizeStackPair = useDockStore((s) => s.resizeStackPair);
	const closeDock = useDockStore((s) => s.close);
	// One dock, many modules: only the CURRENT module's panels render
	// (scope-less panels — terminals — render everywhere).
	const moduleScope = useLocation().pathname.split("/")[1] || "dashboard";
	const cardEls = useRef<Record<string, HTMLDivElement | null>>({});
	const stackEls = useRef<Record<string, HTMLDivElement | null>>({});
	const [draggingId, setDraggingId] = useState<string | null>(null);

	// Esc hides the dock, keeping its panels — carried over from the artifact
	// canvas, whose Esc this replaces. `defaultPrevented` is the whole guard: a
	// kind's own Esc-consuming UI (the artifact's Save dialog, via Modal) listens
	// on `document` and preventDefaults, and document precedes window in the
	// bubble path — so by the time this runs, an inner dismissal has already
	// claimed the key and one Esc must not also take the dock down. The canvas did
	// this by reaching into its own save state; keying off the DOM instead is what
	// lets the host stay kind-blind.
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !e.defaultPrevented) closeDock();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, closeDock]);

	// Pointer-drag resize of the whole dock. Right-anchored, so its width is
	// viewport width − pointer x. The store clamps the minimum.
	const onDockResizeDown = useCallback(
		(e: React.PointerEvent) => {
			e.preventDefault();
			// Viewport px → the dock's own CSS px (the app UI scale zooms the root).
			const onMove = (ev: PointerEvent) => setWidth(toCssPx(window.innerWidth - ev.clientX));
			const onUp = () => {
				window.removeEventListener("pointermove", onMove);
				window.removeEventListener("pointerup", onUp);
				document.body.style.userSelect = "";
			};
			document.body.style.userSelect = "none";
			window.addEventListener("pointermove", onMove);
			window.addEventListener("pointerup", onUp);
		},
		[setWidth],
	);

	/**
	 * One seam drag, both axes. Cards trade height within a column; columns trade
	 * width across the dock — identical maths on a different axis, so it is one
	 * function rather than two that drift apart.
	 *
	 * Clamp in PIXELS, then convert back to weights: the floor is a legibility
	 * fact about the screen, not about the ratio.
	 */
	const dragSeam = useCallback(
		(
			e: React.PointerEvent,
			axis: "y" | "x",
			aId: string,
			bId: string,
			aSize: number,
			bSize: number,
		) => {
			e.preventDefault();
			const els = axis === "y" ? cardEls.current : stackEls.current;
			const apply = axis === "y" ? resizeSlotPair : resizeStackPair;
			const floor = axis === "y" ? MIN_CARD_PX : MIN_STACK_PX;
			const aEl = els[aId];
			const bEl = els[bId];
			if (!aEl || !bEl) return;
			const measure = (el: HTMLElement) => {
				const r = el.getBoundingClientRect();
				return axis === "y" ? r.height : r.width;
			};
			const a0 = measure(aEl);
			const b0 = measure(bEl);
			const total = a0 + b0;
			const totalWeight = aSize + bSize;
			// Not enough room for two legible panes — dragging could only produce a
			// pair that can't be dragged apart again.
			if (total < floor * 2 || totalWeight <= 0) return;
			const start = axis === "y" ? e.clientY : e.clientX;

			const onMove = (ev: PointerEvent) => {
				const wanted = a0 + ((axis === "y" ? ev.clientY : ev.clientX) - start);
				const aPx = Math.max(floor, Math.min(wanted, total - floor));
				apply(aId, bId, (totalWeight * aPx) / total, (totalWeight * (total - aPx)) / total);
			};
			const onUp = () => {
				window.removeEventListener("pointermove", onMove);
				window.removeEventListener("pointerup", onUp);
				document.body.style.userSelect = "";
			};
			document.body.style.userSelect = "none";
			window.addEventListener("pointermove", onMove);
			window.addEventListener("pointerup", onUp);
		},
		[resizeSlotPair, resizeStackPair],
	);

	// Grab a card by its top-centre handle and move it — within a column, across
	// columns, or past the last column to make a new one. Live reorder: the card
	// under the pointer yields as you cross it, so the dock always shows the
	// layout you would get by letting go — no ghost, no drop preview to keep in
	// sync with reality.
	const onGrabDown = useCallback((e: React.PointerEvent, slotId: string) => {
		e.preventDefault();
		setDraggingId(slotId);

		const onMove = (ev: PointerEvent) => {
			const cur = useDockStore.getState().stacks;
			// Hit-test against live rects rather than a cached list: every move this
			// drag makes reflows the dock, so anything measured at pointerdown is
			// stale by the second frame.
			const stackIdx = cur.findIndex((st) => {
				const el = stackEls.current[st.id];
				if (!el) return false;
				const r = el.getBoundingClientRect();
				return ev.clientX >= r.left && ev.clientX <= r.right;
			});

			if (stackIdx < 0) {
				// Past the right edge of every column = make a new one. This is the
				// ONLY way a second column comes into existence, so it has to be
				// reachable by the same gesture rather than a separate affordance.
				const last = cur[cur.length - 1];
				const lastEl = last ? stackEls.current[last.id] : null;
				if (lastEl && ev.clientX > lastEl.getBoundingClientRect().right) {
					useDockStore.getState().moveSlot(slotId, cur.length, 0);
				}
				return;
			}

			const dest = cur[stackIdx]!;
			// Which card in that column is under the pointer? Past its last card
			// means "append".
			let overIdx = dest.slots.findIndex((sl) => {
				const el = cardEls.current[sl.id];
				if (!el) return false;
				const r = el.getBoundingClientRect();
				return ev.clientY >= r.top && ev.clientY <= r.bottom;
			});
			if (overIdx < 0) overIdx = dest.slots.length;

			// Already there — moving a card onto itself would thrash the store every
			// frame for no visible change.
			if (dest.slots[overIdx]?.id === slotId) return;
			useDockStore.getState().moveSlot(slotId, stackIdx, overIdx);
		};
		const onUp = () => {
			setDraggingId(null);
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
			document.body.style.userSelect = "";
		};
		document.body.style.userSelect = "none";
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	}, []);

	if (!open) return null;

	const visible = visibleStacks({ stacks, maximizedSlotId }, moduleScope);
	// Weights are RATIOS, so normalise before handing them to flex. CSS only
	// distributes ALL the free space when the grow values sum to >= 1: at 0.5 it
	// hands out half and leaves the rest blank. Two splits leave a column at
	// 0.25 + 0.25, so the cards filled exactly half their column and the dock
	// showed a large dead gap — every store weight correct, every test green.
	const stackTotal = visible.reduce((n, st) => n + st.size, 0) || 1;
	// A lone card has nowhere to go, and a maximized one is the only card drawn —
	// in both cases the handle would be an affordance that does nothing.
	const canMove = visible.reduce((n, st) => n + st.slots.length, 0) > 1;

	return (
		<aside className={styles.dock} style={{ width }} aria-label="Dock">
			<div
				className={styles.resizer}
				onPointerDown={onDockResizeDown}
				role="separator"
				aria-orientation="vertical"
				aria-label="Resize dock"
			/>
			{visible.length === 0 ? (
				// No stopgap exit here: the rail is always visible beside the dock and
				// owns hide/show, so the empty state is not a dead-end.
				<EmptyState
					icon="🗂️"
					title="Nothing open"
					hint={"Panels open here — a terminal, a browser, files, or a document from the chat.\n\nSeveral at once, as stacked cards."}
				/>
			) : (
				<div className={styles.grid}>
					{visible.map((stack, si) => {
						const nextStack = visible[si + 1];
						return (
							<Fragment key={stack.id}>
								<div
									// flexBasis:0 makes grow the SOLE sizer, so the weights mean
									// what they say instead of being an offset on content width.
									style={{ flexGrow: stack.size / stackTotal, flexBasis: 0 }}
									className={styles.stack}
									ref={(el) => {
										stackEls.current[stack.id] = el;
									}}
								>
									<StackCards
										stack={stack}
										kinds={kinds}
										canMove={canMove}
										draggingId={draggingId}
										cardEls={cardEls}
										onGrab={onGrabDown}
										onSeam={dragSeam}
									/>
								</div>
								{/* The seam IS the gap between two columns — a sibling, never a
								    child, or it would inflate the very width the drag measures. */}
								{nextStack && (
									<div
										className={styles.stackSeam}
										onPointerDown={(e) =>
											dragSeam(e, "x", stack.id, nextStack.id, stack.size, nextStack.size)
										}
										role="separator"
										aria-orientation="vertical"
										aria-label="Resize columns"
									/>
								)}
							</Fragment>
						);
					})}
				</div>
			)}
		</aside>
	);
}

/** One column's cards, with the draggable seams between them. */
function StackCards({
	stack,
	kinds,
	canMove,
	draggingId,
	cardEls,
	onGrab,
	onSeam,
}: {
	stack: DockStack;
	kinds: DockKindMap;
	canMove: boolean;
	draggingId: string | null;
	cardEls: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
	onGrab: (e: React.PointerEvent, slotId: string) => void;
	onSeam: (
		e: React.PointerEvent,
		axis: "y" | "x",
		aId: string,
		bId: string,
		aSize: number,
		bSize: number,
	) => void;
}) {
	// Same normalisation as the columns, one axis over — see the note there.
	const total = stack.slots.reduce((n, sl) => n + sl.size, 0) || 1;
	return (
		<>
			{stack.slots.map((slot, i) => {
				const next = stack.slots[i + 1];
				return (
					<Fragment key={slot.id}>
						<div
							style={{ flexGrow: slot.size / total, flexBasis: 0 }}
							className={styles.cardSlot}
							ref={(el) => {
								cardEls.current[slot.id] = el;
							}}
						>
							<DockCard
								slot={slot}
								kinds={kinds}
								onGrab={canMove ? (e) => onGrab(e, slot.id) : undefined}
								dragging={draggingId === slot.id}
							/>
						</div>
						{/* Absent after the last card: nothing below to trade height with. */}
						{next && (
							<div
								className={styles.seam}
								onPointerDown={(e) => onSeam(e, "y", slot.id, next.id, slot.size, next.size)}
								role="separator"
								aria-orientation="horizontal"
								aria-label="Resize panels"
							/>
						)}
					</Fragment>
				);
			})}
		</>
	);
}
